import { getIsoDateStringAsDate, formatDate, getUkCalendarDateStringFromDate } from "../date-time";
import { getReferenceDataItemFromList } from "../reference-data";

let annotationColours = ['rgb(0, 151, 181)', 'rgb(206, 105, 36)', 'rgb(142, 36, 170)'];

export function getChartAnnotations(yearOnYear, futureDays, isTriggerDatesDisplayed, triggerDates, isChangeDatesDisplayed, history, isDischargesDisplayed, controlShowAnnotationLabels, chartWidth, regimeList, dischargePeriod) {
    if (yearOnYear) {
        return getYearOnYearChartAnnotations(isTriggerDatesDisplayed, triggerDates, controlShowAnnotationLabels, chartWidth);
    } else {
        return getDateRangeChartAnnotations(futureDays, isTriggerDatesDisplayed, triggerDates, isChangeDatesDisplayed, history, isDischargesDisplayed, controlShowAnnotationLabels, chartWidth, regimeList, dischargePeriod);
    }
}

function getYearOnYearChartAnnotations(isTriggerDatesDisplayed, triggerDates, controlShowAnnotationLabels, chartWidth) {
    let annotations = {};

    if (isTriggerDatesDisplayed && triggerDates && triggerDates.length > 0) {

        let labelPosition = 96;

        triggerDates.forEach((triggerDate, index) => {
            let labelTooltip;
            if (triggerDate.notes && triggerDate.notes != 'No comments') {
                labelTooltip = formatDate(triggerDate.date).concat(": ", triggerDate.notes);
            } else {
                labelTooltip = formatDate(triggerDate.date).concat(": ", triggerDate.displayType);
            }

            // Add this trigger date to the list of chart annotations 
            annotations[triggerDate.type.concat(index)] = addVerticalLineAnnotation(
                formatDate(triggerDate.date).slice(0,5), annotationColours[0], 
                { display : controlShowAnnotationLabels, content : triggerDate.displayType, position : labelPosition + '%', backgroundColor : annotationColours[0] },
                labelTooltip, chartWidth);

            // Decrement or reset the annotation label position to avoid overlaps 
            labelPosition == 8 ? labelPosition = 96 : labelPosition -= 8;
        });
    }

    return annotations;
}

function getDateRangeChartAnnotations(futureDays, isTriggerDatesDisplayed, triggerDates, isChangeDatesDisplayed, history, isDischargesDisplayed, controlShowAnnotationLabels, chartWidth, regimeList, dischargePeriod) {
    let annotations = {};

    // Set the date limits of the graph to determine which annotations can be displayed
    let dataStartDate = getIsoDateStringAsDate(history[history.length - 1].date);
    let dataFinishDate;
    // If the future days is greater than 0, set the finish date to the current date plus the future days
    if (futureDays > 0) {
        dataFinishDate = new Date();
        dataFinishDate.setDate(dataFinishDate.getDate() + futureDays);
    // Otherwise, set the finish date to the last date in the history
    } else {
        dataFinishDate = getIsoDateStringAsDate(history[0].date);
    }

    // Add any applicable trigger dates to the chart as annotations
    if (isTriggerDatesDisplayed && triggerDates && triggerDates.length > 0) {
        let labelPositions = [90, 74, 58, 42, 26, 11];
        let labelPositionIndex = 0;

        triggerDates.forEach((triggerDate, index) => {
            for (let i = dataStartDate.getFullYear(); i <= (new Date()).getFullYear(); i++) {
                
                let annualTriggerDate = getIsoDateStringAsDate(triggerDate.date)

                const today = new Date();
                today.setHours(0,0,0,0);

                // Don't plot TD points for any years before the event occured unless the trigger date is in the future
                if ((i >= annualTriggerDate.getFullYear()) || (getIsoDateStringAsDate(triggerDate.date) >= today)) 
                {
                    annualTriggerDate.setFullYear(i);

                    // After applying the 'current' year to the trigger date, does it fall within the graph dates
                    if (annualTriggerDate >= dataStartDate && annualTriggerDate <= dataFinishDate) {
                        let labelTooltip;
                        if (triggerDate.notes && triggerDate.notes != 'No comments') {
                            labelTooltip = formatDate(triggerDate.date).concat(": ", triggerDate.notes);
                        } else {
                            labelTooltip = formatDate(triggerDate.date).concat(": ", triggerDate.displayType);
                        }
                        
                        annotations[triggerDate.type.concat(i,index)] = addVerticalLineAnnotation(
                            getUkCalendarDateStringFromDate(annualTriggerDate), annotationColours[0],
                            { display : controlShowAnnotationLabels, content : triggerDate.displayType, position : labelPositions[labelPositionIndex] + '%', backgroundColor : annotationColours[0] },
                            labelTooltip, chartWidth);

                        // Decrement or reset the annotation label position to avoid overlaps 
                        (labelPositionIndex == (labelPositions.length - 1)) ? labelPositionIndex = 0 : labelPositionIndex++;
                    }
                }
            }
        });            
    }

    // Add any applicable change of circumstances dates to the chart as annotations
    if (isChangeDatesDisplayed) {
        let changeDates = mapChangeDates(history, regimeList);

        if (changeDates && changeDates.length > 0) {
            let labelPositions = [98, 82, 66, 50, 34, 18, 4];
            let labelPositionIndex = 0;

            changeDates.forEach((changeDate, index) => {
                if (changeDate.date >= dataStartDate && changeDate.date <= dataFinishDate) {
                    // Add this circumstance change date to the list of chart annotations 
                    annotations[changeDate.type.concat(index)] = addVerticalLineAnnotation(
                        getUkCalendarDateStringFromDate(changeDate.date), annotationColours[1], 
                        { display : controlShowAnnotationLabels, content : changeDate.type, position : labelPositions[labelPositionIndex] + '%', backgroundColor : annotationColours[1] },
                        changeDate.notes, chartWidth);
                    
                    // Decrement or reset the annotation label position to avoid overlaps 
                    (labelPositionIndex == (labelPositions.length - 1)) ? labelPositionIndex = 0 : labelPositionIndex++;
                }
            });
        }
    }

    if (isDischargesDisplayed) {
        let dischargePeriods = mapDischargeDates(history, dischargePeriod);
        if (dischargePeriods && dischargePeriods.length > 0) {

            dischargePeriods.forEach((dischargePeriod, index) => {
                // Add this temporary discharge period to the list of chart annotations 
                annotations['discharge'.concat(index)] = addBoxAnnotation(dischargePeriod);
            });
        }
    }

    return annotations;
}

function mapChangeDates(history, regimeList) {
    let changeDates = []; 

    let current = { day : history[0].date, regime : history[0].regimeLevel, keyworker : history[0].keyworkerName, 
        status : history[0].status, cell : history[0].cell, wing : history[0].wing, unit : history[0].unit }

    history.forEach(record => {        
        if (record.regimeLevel != current.regime) {
            changeDates.unshift({ date : getIsoDateStringAsDate(current.day), type: 'Regime',
                notes : formatDate(current.day).concat(": From ", getReferenceDataItemFromList(record.regimeLevel, regimeList, 'description', 'level'), " to ", getReferenceDataItemFromList(current.regime, regimeList, 'description', 'level'))});
            current.regime = record.regimeLevel;
        }
        if (record.keyworkerName != current.keyworker) {
            changeDates.unshift({ date : getIsoDateStringAsDate(current.day), type : 'Keyworker',
                notes : formatDate(current.day).concat(": From ", (record.keyworkerName ? record.keyworkerName : 'Not recorded'), " to ", (current.keyworker ? current.keyworker : 'Not recorded')) });
            current.keyworker = record.keyworkerName;
        }
        if (record.status != current.status) {
            changeDates.unshift({ date : getIsoDateStringAsDate(current.day), type : 'Status',
                notes : formatDate(current.day).concat(": From ", (record.status ? record.status : 'Not recorded'), " to ", (current.status ? current.status : 'Not recorded')) });
            current.status = record.status;
        }
        if (record.unit != current.unit || record.wing != current.wing || record.cell != current.cell) {
            changeDates.unshift({ date : getIsoDateStringAsDate(current.day), type: 'Cell',
                notes : formatDate(current.day).concat(": From ", (record.unit === null && record.wing === null && record.cell === null  ? 'Not recorded' : (record.unit ? record.unit + '·' : '') + record.wing + "·" + record.cell), " to ", (current.unit + current.wing + current.cell == null ? 'Not recorded' : (current.unit ? current.unit + '·' : '') + current.wing + "·" + current.cell))});
            current.unit = record.unit;
            current.wing = record.wing;
            current.cell = record.cell;
        }

        current.day = record.date;
    });

    return changeDates;
}

function mapDischargeDates(history, dischargePeriod) {
    let dischargeDates = []; 

    let temporaryDischargeStartDate = null;
    let dischargedDaysCount = 0;
    
    // Iterate through the history in reverse order to find the temporary discharge periods
    for (let i = (history.length - 1); i >= 0; i--) {
        if (history[i].indicatorTemporaryDischarged) {
            if (temporaryDischargeStartDate == null) {
                temporaryDischargeStartDate = formatDate(history[i].date);
            }

            dischargedDaysCount++;
        } else {
            if (dischargedDaysCount > dischargePeriod) {
                dischargeDates.push({start : temporaryDischargeStartDate, end : formatDate(history[i].date)});               
            }

            temporaryDischargeStartDate = null;
            dischargedDaysCount = 0; 
        }
    }

    // If the inmate is still discharged at the end of the history, add the discharge period to the list of annotations
    if (temporaryDischargeStartDate != null && dischargedDaysCount > dischargePeriod) {
        dischargeDates.push({start : temporaryDischargeStartDate, end : formatDate(history[0].date)});
    }

    return dischargeDates;
}

function addVerticalLineAnnotation(date, color, labelProps, labelTooltip, chartWidth) {
    return {
        type: 'line',
        xMin: date,
        xMax: date,
        borderColor: color,
        borderDash: [10, 10],
        label: {...labelProps, padding: {x: 6, y: 3}, font: {size: 11, weight: 'normal'} },
        enter: function ({element}) {
            let labelWidth = getAnnotationLabelWidth(labelTooltip);
            let labelPosition = getChartAnnotationLabelPosition(chartWidth, labelWidth, element.x);
            element.label.width = labelWidth;
            element.label.x = labelPosition;
            element.label.options.content = labelTooltip; 
            return true;
        },
        leave: function ({element}) {
            let labelWidth = getAnnotationLabelWidth(labelProps.content);
            let labelPosition = getChartAnnotationLabelPosition(chartWidth, labelWidth, element.x);
            element.label.width = labelWidth;
            element.label.x = labelPosition;
            element.label.options.content = labelProps.content;
            return true;
        }
    }
}

function addBoxAnnotation(dischargePeriod) {
    return {
        type: 'box',
        xMin: dischargePeriod.start,
        xMax: dischargePeriod.end,
        backgroundColor: 'rgba(0, 0, 0, 0.10)',
        borderWidth: 0
    }
}

function getAnnotationLabelWidth(text) {
    let tempCanvas = document.createElement("canvas");
    let ctx = tempCanvas.getContext('2d');
    return ctx.measureText(text).width * (text.length > 10 ? 1.25 : 1.5);
};

function getChartAnnotationLabelPosition(chartWidth, labelWidth, xPos) {
    if (xPos + (labelWidth / 2) > (chartWidth - 20)) {
        let labelLeftPosition = xPos - (labelWidth / 2);
        let overflow = (xPos + (labelWidth / 2)) - (chartWidth - 20);
        return labelLeftPosition - overflow - 5;
    } else if (xPos - (labelWidth / 2) < 76) {
        let labelLeftPosition = xPos - (labelWidth / 2);
        let overflow = 76 - (xPos - (labelWidth / 2));
        return labelLeftPosition + overflow + 5;
    } else {
        return xPos - (labelWidth / 2);
    }
}